import { api } from './client';
import type {
  HelpPostSummary,
  MyPost,
  Page,
  PostDetail,
  PostDuration,
  PostKind,
  PostPoster,
  PostRequirement,
} from './types';

export type BrowseSort = 'recent' | 'distance' | 'trust';

export interface BrowseFilters {
  kind?: PostKind;
  category?: string;
  sort?: BrowseSort;
  /** specs/003 #1: only posts within this many km. */
  within?: BrowseRadius;
}

export const BROWSE_RADII = [3, 10, 25] as const;
export type BrowseRadius = (typeof BROWSE_RADII)[number];

export interface CreatePostInput {
  kind: PostKind;
  title: string;
  category: string;
  description: string;
  /** When it is needed or available (ISO); null takes it back on an edit. */
  neededBy?: string | null;
  urgent?: boolean;
  duration?: PostDuration | null;
  photoUrl?: string | null;
  peopleNeeded?: number | null;
  requirements?: PostRequirement[];
  limits?: string | null;
}

/** The query string Android sends for GET /posts (mobile/src/services/postsApi.ts). */
export function browseQuery(
  filters: BrowseFilters,
  page?: { page: number; pageSize: number },
): string {
  const params = new URLSearchParams();
  if (filters.kind) params.set('kind', filters.kind);
  if (filters.category) params.set('category', filters.category);
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.within) params.set('within', String(filters.within));
  if (page) {
    params.set('page', String(page.page));
    params.set('pageSize', String(page.pageSize));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * specs/003 #5: a neighbour's offer on a post. The poster accepts or declines it; only an accepted offer opens a
 * chat (`conversationId`).
 */
export interface HelpOffer {
  id: string;
  postId: string;
  status: 'pending' | 'accepted' | 'declined' | 'withdrawn';
  message: string | null;
  conversationId: string | null;
  /** Once accepted: the commitment that tracks the help to its end (finish it, thank the helper). */
  commitmentId?: string | null;
  createdAt: string;
  decidedAt: string | null;
  member: PostPoster | null;
}

/** The offers waiting on the member's own open posts: in all, not yet opened, and per post. */
export interface OffersWaiting {
  waiting: number;
  unseen: number;
  byPost: Record<string, { waiting: number; unseen: number }>;
}

/** FR-007–FR-012: help posts (tasks.md T090). */
export const postsApi = {
  create: (input: CreatePostInput) => api.post<HelpPostSummary>('/posts', input),

  browsePage: (filters: BrowseFilters, page: number, pageSize: number) =>
    api.get<Page<HelpPostSummary>>(`/posts${browseQuery(filters, { page, pageSize })}`),

  /** The member's own posts of both kinds, open, in progress and done, newest first. */
  listMine: () => api.get<MyPost[]>('/posts/mine'),

  getDetail: (postId: string) => api.get<PostDetail>(`/posts/${postId}`),

  update: (postId: string, input: Partial<Omit<CreatePostInput, 'kind'>>) =>
    api.patch<HelpPostSummary>(`/posts/${postId}`, input),

  remove: (postId: string) => api.delete<undefined>(`/posts/${postId}`),

  /** For the badge and the counts on the member's post cards. */
  offersWaiting: () => api.get<OffersWaiting>('/interests/waiting'),

  /** The poster gets every offer on their post; anyone else only their own. */
  listOffers: (postId: string) => api.get<HelpOffer[]>(`/posts/${postId}/interests`),
  offer: (postId: string, message?: string) =>
    api.post<HelpOffer>(`/posts/${postId}/interests`, message ? { message } : {}),
  acceptOffer: (offerId: string) => api.post<HelpOffer>(`/interests/${offerId}/accept`),
  declineOffer: (offerId: string) => api.post<HelpOffer>(`/interests/${offerId}/decline`),
  withdrawOffer: (offerId: string) => api.delete<HelpOffer>(`/interests/${offerId}`),

  report: (postId: string, reason: string, details?: string) =>
    api.post(`/posts/${postId}/reports`, { targetType: 'post', reason, details }),
};
